/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import {
	useState,
	useLayoutEffect,
	useRef,
	useCallback,
} from 'react';
import { useNavigate } from 'react-router';

// Mantine
import { Box } from '@mantine/core';

// Layouts
import { Header } from '@layouts/Header';

// Styles
import classes from './measurability-projects.module.css';

// Mantine
import {
	Paper,
	Group,
	Image,
	Text,
	Skeleton,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { isNil } from 'lodash';

// UI local components
import {
	AddFiltersButton,
	Searchbar,
	Pagination,
} from '@components/atoms';

import {
	CardProject,
	MenuFilter,
} from '@components/molecules';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';

// Icons
import {
	TParsedMesurabilityProject,
	useMeasurabilityProjects,
} from '@features/catalogs/meausrability-projects/list/api/get-public-measurability-projects';
import { useMeasurabilityProject } from '@features/catalogs/meausrability-projects/list/hooks/useMeasurabilityProjects';

// Constants
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { PROJECT_TECHNOLOGIES } from '@config/constants/project-technologies';

// Helpers
import { useCountryCodes } from 'shared/api/get-country-codes';

// Types
import { TApiResponseMeasurabilityProjectPopulated } from '@global-types/api/api';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const MeasurabilityProjectsPublic = () => {
	/* ********************************** Hooks ********************************* */
	const { t } = useTranslation();

	// State to manage overflow filters visibility
	const [isOverflowFilters] = useState(false);

	// State to manage scroll filters visibility
	const [showScrollFilters, setShowScrollFilters] =
		useState(false);

	// Ref for the container element
	const containerRef = useRef<HTMLDivElement>(null);

	// Routing
	const navigate = useNavigate();
	const { data: countryCodes } = useCountryCodes(
		'measurabilities'
	);

	// Filter countries based on fetched country codes
	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? EUROPEAN_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	// Custom hook for measurability project logic
	const {
		handleChangePaginationSize,
		handleChangePagination,
		measurabilityProjectsPage,
		measurabilityProjectsPageSize,
		measurabilityProjectsQueryParams,
		handleTechnologyFilter,
		handleCountryFilter,
		handleProjectNameFilter,
	} = useMeasurabilityProject();

	// APIs
	const {
		data: measurabilityProjects,
		total: totalItems,
		totalPages,
		isFetching,
	} = useMeasurabilityProjects(
		measurabilityProjectsPage,
		measurabilityProjectsPageSize,
		measurabilityProjectsQueryParams
	);

	// Hook to get the size of the container
	const { ref, width: widthContainer } = useElementSize();

	/* ****************************** SIDE-EFFECTS ****************************** */
	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	/* ******************************** CALLBACKS ******************************* */
	// Function to handle scroll behavior
	const handleScroll = useCallback((scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	}, []);

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.layout}>
			<Box className={classes.wrapper}>
				<Box className={classes.content}>
					<Header
						id={''}
						label={t('global.operationalProject')}
						user={null}
						onLogout={() => {}}
					/>
					{/*Main */}
					<Box className={classes.body}>
						<Box className={classes.container} ref={ref}>
							<Paper className={classes.paper}>
								{/* Row filters */}
								<div className={classes.containerFilters}>
									<div className={classes.wrapperRowLeft}>
										{!isOverflowFilters &&
											showScrollFilters && (
												<Box
													className={
														classes.wrapperIconScrollToRight
													}
													variant="transparent"
													onClick={() => {
														handleScroll('left');
													}}
												>
													<Image
														className={
															classes.containerIconScrollToLeft
														}
														src={ArrowScrollIcon}
													/>
												</Box>
											)}
										<div
											ref={containerRef}
											className={classes.wrapperFilters}
										>
											<Group gap={'0px'}>
												<Box
													className={
														classes.containerFilters5
													}
												>
													<Box
														className={
															classes.spacerFilter
														}
													/>
													<Box
														className={
															classes.containerFilterAutocomplete
														}
													>
														<Searchbar
															hiddenFrom="xxl"
															placeholder={t(
																'global.search'
															)}
															size={'sm'}
															onChange={
																handleProjectNameFilter
															}
														/>
														<Searchbar
															visibleFrom="xxl"
															placeholder={t(
																'global.search'
															)}
															size={'lg'}
															onChange={
																handleProjectNameFilter
															}
														/>
													</Box>
													<Box
														className={
															classes.spacerFilter
														}
													/>
													<Box
														className={
															classes.containerFilterSelect
														}
													>
														<MenuFilter
															value={
																measurabilityProjectsQueryParams[
																	'location.countryCode'
																]
																	? measurabilityProjectsQueryParams[
																			'location.countryCode'
																		]['$in']
																	: []
															}
															isPersist
															onApply={
																handleCountryFilter
															}
															label={t('global.country')}
															size="lg"
															options={filteredCountries}
															placeholderSearch={t(
																'filters.placeholderSearch.searchCountry'
															)}
															placeholder={t(
																'filters.placeholders.selectCountry'
															)}
														/>
													</Box>
													<Box
														className={
															classes.spacerFilter
														}
													/>
													<Box
														className={
															classes.containerFilterSelect
														}
													>
														<MenuFilter
															isPersist
															value={
																measurabilityProjectsQueryParams.technology
																	? measurabilityProjectsQueryParams.technology
																	: []
															}
															onApply={
																handleTechnologyFilter
															}
															size="lg"
															options={
																PROJECT_TECHNOLOGIES
															}
															placeholderSearch={t(
																'filters.placeholderSearch.searchTechnology'
															)}
															placeholder={t(
																'filters.placeholders.selectTechnology'
															)}
														/>
													</Box>
													<Box
														className={
															classes.spacerFilter
														}
													/>
													<Box
														className={
															classes.containerFilterNumeric
														}
													>
														<AddFiltersButton>
															{t('global.addFilters')}
														</AddFiltersButton>
													</Box>
												</Box>
											</Group>
										</div>
										{!isOverflowFilters &&
											showScrollFilters && (
												<Box
													className={
														classes.wrapperIconScrollToRight
													}
													variant="transparent"
													onClick={() => {
														handleScroll('right');
													}}
												>
													<Image
														className={
															classes.containerIconScrollToRight
														}
														src={ArrowScrollIcon}
													/>
												</Box>
											)}
									</div>
								</div>
								<Box className={classes.contentCards}>
									{isFetching ? (
										<Box
											className={
												classes.containerSkeletons
											}
										>
											{Array.from(Array(5).keys()).map(
												(index) => (
													<Skeleton
														key={index}
														height={260}
														width={260}
														radius="md"
													/>
												)
											)}
										</Box>
									) : Array.isArray(
											measurabilityProjects
									  ) &&
									  measurabilityProjects.length > 0 ? (
										measurabilityProjects.map(
											(
												project: TParsedMesurabilityProject
											) => (
												<CardProject
													key={project.id}
													size={'responsive'}
													type={'view-measurability'}
													data={project}
													isAdded={false}
													tco2={2}
													onAdd={() => {}}
													onChangeTco2={() => {}}
													handleShowMoreDetails={() => {
														navigate(
															`/catalog-measurabilities/${project.id}`
														);
													}}
													inactive={!project?.active}
												/>
											)
										)
									) : (
										<Box
											className={
												classes.containerEmptyList
											}
										>
											<Box
												className={
													classes.containerTextsNoRecords
												}
											>
												<Image
													src={NoRecordsIcon}
													className={
														classes.iconNoRecords
													}
												/>
												<>
													<Text
														className={
															classes.titleNoRecords
														}
													>
														{t(
															'global.datatable.noResultsFound'
														)}
													</Text>
													<Text
														className={
															classes.descriptionNoRecords
														}
													>
														{t(
															'global.datatable.noResultsFoundDescription'
														)}
													</Text>
												</>
											</Box>
										</Box>
									)}
								</Box>
							</Paper>
							{/* Pagination */}
							<Box
								className={classes.containerPagination}
							>
								<Pagination
									pagesize={measurabilityProjectsPageSize}
									totalitems={totalItems}
									activepage={measurabilityProjectsPage}
									handlechangesize={
										handleChangePaginationSize
									}
									onChange={(data) =>
										handleChangePagination(data)
									}
									total={totalPages ? totalPages : 0}
								/>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default MeasurabilityProjectsPublic;
